import { useAuth } from '@/auth/useAuth';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useOrganizationPerms } from '@/organization/useOrganizationRole';
import { useOrganizationDepartments } from '@/organization/useOrganizations';
import {
  archiveOrganizationUser,
  fetchOrganizationUsers,
  restoreOrganizationUser,
} from '@/services/userService';
import type { Member } from '@/types/user';
import CancelIcon from '@mui/icons-material/Cancel';
import EditIcon from '@mui/icons-material/Edit';
import RestoreFromTrashIcon from '@mui/icons-material/RestoreFromTrash';
import {
  Box,
  Checkbox,
  Chip,
  FormControlLabel,
  IconButton,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

interface Props {
  organizationId: string;
  onEditUser: (user: Member) => void;
}

// TODO: Update backend naming from "archive" to "inactivate" to match frontend
export function UsersList({ organizationId, onEditUser }: Props) {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [department, setDepartment] = useState('');
  const [confirmUser, setConfirmUser] = useState<Member | null>(null);
  const { user: _currentUser } = useAuth();
  const { data: perms } = useOrganizationPerms(organizationId);
  const canManageUsers = perms?.canManageUsers;

  const queryClient = useQueryClient();

  const { data: departments } = useOrganizationDepartments(organizationId);

  const usersQuery = useQuery({
    queryKey: [
      'organization-users',
      organizationId,
      page,
      search,
      showArchived,
      department,
    ],
    queryFn: () =>
      fetchOrganizationUsers({
        organizationId,
        page,
        search,
        showArchived,
        department,
      }),
  });

  const archiveMutation = useMutation({
    mutationFn: async (user: Member) => {
      if (user.isArchived) {
        return restoreOrganizationUser(organizationId, user.userDetails.uuid);
      }
      return archiveOrganizationUser(organizationId, user.userDetails.uuid);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['organization-users', organizationId],
      });
      setConfirmUser(null);
    },
  });

  return (
    <Paper sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
        <TextField
          label='Search users...'
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size='small'
          sx={{ flexGrow: 1, mt: '3px' }}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
            />
          }
          label='Show inactive'
          sx={{ my: 0, ml: 1 }}
        />
        <Select
          size='small'
          displayEmpty
          value={department}
          onChange={(e) => setDepartment(e.target.value)}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value=''>All Departments</MenuItem>
          {departments?.map((d) => (
            <MenuItem key={d.id} value={d.id}>
              {d.name}
            </MenuItem>
          ))}
        </Select>
      </Box>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Department</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Status</TableCell>
              {canManageUsers && <TableCell align='right'>Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {usersQuery.data?.results.map((user, index) => {
              // Use email as primary key since UUID can be null
              // const userGroup = getUserGroup(UUID)??
              const userKey =
                user.id || user.userDetails.email || `user-${index}`;
              return (
                <TableRow key={userKey}>
                  <TableCell>{`${user.userDetails.firstName} ${user.userDetails.lastName}`}</TableCell>
                  <TableCell>{user.userDetails.email}</TableCell>
                  <TableCell>
                    {user.userDetails.departments
                      ?.map((d) => d.name)
                      .join(', ')}
                  </TableCell>
                  <TableCell>{user.role}</TableCell>
                  <TableCell>
                    <Chip
                      label={user.isArchived ? 'Inactive' : 'Active'}
                      size='small'
                      color={user.isArchived ? 'default' : 'success'}
                    />
                  </TableCell>
                  {canManageUsers && (
                    <TableCell align='right'>
                      <Tooltip title={'Edit'}>
                        <IconButton
                          onClick={() => onEditUser(user)}
                          size='small'
                        >
                          <EditIcon color={'info'} />
                        </IconButton>
                      </Tooltip>
                      <IconButton
                        onClick={() => setConfirmUser(user)}
                        size='small'
                        color='warning'
                        aria-label={
                          user.isArchived ? 'Restore user' : 'Inactivate user'
                        }
                      >
                        {user.isArchived ? (
                          <Tooltip title={'Restore'}>
                            <RestoreFromTrashIcon />
                          </Tooltip>
                        ) : (
                          <Tooltip title={'Inactivate'}>
                            <CancelIcon />
                          </Tooltip>
                        )}
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <Pagination
        count={Math.ceil((usersQuery.data?.count || 0) / 20)}
        page={page}
        onChange={(_, value) => setPage(value)}
        sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}
      />

      <ConfirmDialog
        open={!!confirmUser}
        title={confirmUser?.isArchived ? 'Restore User' : 'Inactivate User'}
        message={`Are you sure you want to ${confirmUser?.isArchived ? 'restore' : 'inactivate'} ${confirmUser?.userDetails.email}?`}
        onConfirm={() => confirmUser && archiveMutation.mutate(confirmUser)}
        onCancel={() => setConfirmUser(null)}
        confirmColor={confirmUser?.isArchived ? 'primary' : 'error'}
        confirmText={confirmUser?.isArchived ? 'Restore' : 'Inactivate'}
      />
    </Paper>
  );
}
